import React, { useState, useMemo, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { FiRefreshCw } from "react-icons/fi";
import { FaChevronUp, FaChevronDown } from "react-icons/fa";
import { useExport } from "../../hooks/useExport";
import { nseIndicesOptions } from "../Helper/Helperdata";
import { useMarketSnapshot } from "../../context/MarketSnapshotContext";
import useLivePrices from "../../hooks/useLivePrices";
import { useIndexSymbols } from "../../context/IndexSymbolContext";
import { InfoTooltip } from "../Helper/InfoToolTip";
import { TOOLTIPS } from "../../helper/tooltilData";

const VOLUME_THRESHOLDS = [
  { label: "1.5x+", value: 1.5 },
  { label: "2x+", value: 2.0 },
  { label: "2.5x+", value: 2.5 },
];

const HighVolumeScreener = () => {
  const navigate = useNavigate();
  const [selectedIndex, setSelectedIndex] = useState(
    () => localStorage.getItem("highVolIndex") || "NIFTY 50",
  );
  const [minRvol, setMinRvol] = useState(1.5);
  const [sortKey, setSortKey] = useState("relativeVolume");
  const [sortDir, setSortDir] = useState("desc");

  const { indexSymbols } = useIndexSymbols();
  const { exportCSV, exportExcel, exporting } = useExport();
  const {
    loadDashboardIndexMarket,
    dashboardMarketData,
    resetDashboardMarketData,
    dashboardLoading,
  } = useMarketSnapshot();
  const { prices: livePrices, subscribeSymbols } = useLivePrices();

  useEffect(() => {
    if (!selectedIndex) return;
    localStorage.setItem("highVolIndex", selectedIndex);
    // const symbols = indexSymbols?.[selectedIndex] || [];
    let symbols = [];
    if (selectedIndex === "ALL") {
      // union of every index's symbols, deduped
      const set = new Set();
      Object.values(indexSymbols || {}).forEach((list) => {
        (list || []).forEach((s) => s && set.add(s));
      });
      symbols = [...set];
    } else {
      symbols = indexSymbols?.[selectedIndex] || [];
    }
    if (symbols.length) subscribeSymbols(symbols);
    loadDashboardIndexMarket({ indexName: selectedIndex });
  }, [selectedIndex, indexSymbols]);

  const handleIndexChange = (e) => {
    resetDashboardMarketData();
    setSelectedIndex(e.target.value);
  };

  // ── Build rows (same mapping as SmartMoneyScreener) ──
  const allStocks = useMemo(() => {
    const rows = Object.values(dashboardMarketData || {});
    return rows
      .map((item) => {
        const quote = item?.quote?.data;
        const live = livePrices[item?.symbol];
        if (!quote?.ohlc) return null;

        const ltp = live?.ltp ?? quote?.last_price;
        const volume = live?.volume ?? quote?.volume;
        const close = item?.prev_close || quote?.ohlc?.close;
        const averageVolume20 = item?.average_volume_20 || 0;

        if (!ltp) return null;

        const pctChange = close
          ? Number((((ltp - close) / close) * 100).toFixed(2))
          : 0;

        const relativeVolume =
          Number.isFinite(volume) &&
          Number.isFinite(averageVolume20) &&
          averageVolume20 > 0
            ? Number((volume / averageVolume20).toFixed(2))
            : null;

        const sm = item?.smart_money;
        const macdSignal = item?.prediction;

        return {
          symbol: item.symbol,
          securityId: item.security_id,
          company: item.company || "",
          ltp,
          close,
          volume,
          averageVolume20,
          pctChange,
          relativeVolume,
          cmfValue: sm?.cmf?.value ?? null,
          cmfSignal: sm?.cmf?.signal || "—",
          mfiValue: sm?.mfi?.value ?? null,
          mfiSignal: sm?.mfi?.signal || "—",
          smartMoneySignal: sm?.signal || sm?.status || "—",
          macdSignal,
        };
      })
      .filter(Boolean);
  }, [dashboardMarketData, livePrices]);

  // ── Filter by threshold, then sort ──
  const filtered = useMemo(() => {
    return allStocks.filter(
      (s) => s.relativeVolume !== null && s.relativeVolume >= minRvol,
    );
  }, [allStocks, minRvol]);

  const sorted = useMemo(() => {
    return [...filtered].sort((a, b) => {
      const aVal = a[sortKey] ?? -Infinity;
      const bVal = b[sortKey] ?? -Infinity;
      if (typeof aVal === "string")
        return sortDir === "asc"
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      return sortDir === "asc" ? aVal - bVal : bVal - aVal;
    });
  }, [filtered, sortKey, sortDir]);

  // ── Sort header ──
  const SortTh = ({ label, field, align, tooltip }) => (
    <th className={align === "left" ? "sm__th-left" : ""}>
      <div className="sm__sort-head">
        {label}
        {tooltip && <InfoTooltip text={tooltip} />}
        <span className="sm__sort-arrows">
          <FaChevronUp
            size={10}
            className={sortKey === field && sortDir === "asc" ? "active" : ""}
            onClick={() => {
              setSortKey(field);
              setSortDir("asc");
            }}
          />
          <FaChevronDown
            size={10}
            className={sortKey === field && sortDir === "desc" ? "active" : ""}
            onClick={() => {
              setSortKey(field);
              setSortDir("desc");
            }}
          />
        </span>
      </div>
    </th>
  );

  const getRvolClass = (val) => {
    if (val == null) return "sm__clr-neutral";
    if (val >= 2.5) return "sm__clr-rvol-extreme";
    if (val >= 1.5) return "sm__clr-rvol-high";
    if (val >= 0.8) return "sm__clr-rvol-normal";
    return "sm__clr-rvol-low";
  };

  const getSignalTextClass = (signal) => {
    const s = signal?.toLowerCase();
    if (["bullish", "accumulation", "money entering"].includes(s))
      return "sm__clr-bull";
    if (["bearish", "distribution", "money leaving"].includes(s))
      return "sm__clr-bear";
    return "sm__clr-neutral";
  };

  const EXPORT_COLUMNS = [
    { header: "Symbol", key: "symbol", width: 15 },
    { header: "Company", key: "company", width: 20 },
    { header: "LTP", key: "ltp", width: 12 },
    { header: "Chg %", key: "pctChange", width: 10 },
    { header: "Volume", key: "volume", width: 14 },
    { header: "Avg Vol (20d)", key: "averageVolume20", width: 16 },
    { header: "Rel Vol", key: "relativeVolume", width: 12 },
    { header: "CMF", key: "cmfValue", width: 12 },
    { header: "CMF Signal", key: "cmfSignal", width: 14 },
    { header: "MFI", key: "mfiValue", width: 12 },
    { header: "MFI Signal", key: "mfiSignal", width: 14 },
    { header: "Smart Money", key: "smartMoneySignal", width: 14 },
    { header: "MACD Signal", key: "macdSignal", width: 14 },
  ];

  const buildExportRows = () =>
    sorted.map((s) => ({
      symbol: s.symbol,
      company: s.company || "",
      ltp: s.ltp ?? "",
      pctChange: s.pctChange ?? "",
      volume: s.volume ?? "",
      averageVolume20: s.averageVolume20 > 0 ? s.averageVolume20 : "",
      relativeVolume: s.relativeVolume ?? "",
      cmfValue: s.cmfValue ?? "",
      cmfSignal: s.cmfSignal || "",
      mfiValue: s.mfiValue ?? "",
      mfiSignal: s.mfiSignal || "",
      smartMoneySignal: s.smartMoneySignal || "",
      macdSignal: s.macdSignal || "",
    }));

  const handleDownloadCSV = () =>
    exportCSV({
      rows: buildExportRows(),
      columns: EXPORT_COLUMNS,
      filename: `${selectedIndex}_high_volume_rvol${minRvol}`,
      sheetName: selectedIndex,
    });

  const handleDownloadExcel = () =>
    exportExcel({
      rows: buildExportRows(),
      columns: EXPORT_COLUMNS,
      filename: `${selectedIndex}_high_volume_rvol${minRvol}`,
      sheetName: selectedIndex,
    });
  console.log("Dashboard market data", dashboardMarketData);
  return (
    <div className="sm">
      <div className="sm__header">
        <div className="sm__header-left">
          <h1 className="sm__title">High Volume Screener</h1>
          <p className="sm__sub">
            Stocks trading at unusually high volume vs their 20-day average
          </p>
        </div>
        <div className="sm__header-right">
          {/* Threshold pills */}
          <div className="sm__threshold-group">
            {VOLUME_THRESHOLDS.map((t) => (
              <button
                key={t.value}
                className={`sm__threshold-btn ${minRvol === t.value ? "sm__threshold-btn--active" : ""}`}
                onClick={() => setMinRvol(t.value)}
              >
                {t.label}
              </button>
            ))}
          </div>

          <select
            className="sm__index-select"
            value={selectedIndex}
            onChange={handleIndexChange}
          >
            <option value="ALL">All Stocks</option>
            {nseIndicesOptions?.map((idx) => (
              <option key={idx} value={idx}>
                {idx}
              </option>
            ))}
          </select>
          <button
            className="sm__refresh-btn"
            onClick={() =>
              loadDashboardIndexMarket({ indexName: selectedIndex })
            }
          >
            <FiRefreshCw size={14} />
          </button>
          <button
            className="ohl__refresh-btn"
            onClick={handleDownloadCSV}
            disabled={exporting}
          >
            {exporting ? "…" : "CSV ⬇"}
          </button>
          <button
            className="ohl__refresh-btn"
            onClick={handleDownloadExcel}
            disabled={exporting}
          >
            {exporting ? "…" : "Excel ⬇"}
          </button>
        </div>
      </div>

      <div className="sm__result-bar">
        <span className="sm__result-count">
          <b>{sorted.length}</b> stocks with volume ≥ {minRvol}x average
        </span>
        <span className="sm__live-indicator">
          <span className="sm__live-dot" /> Live
        </span>
      </div>

      <div className="sm__table-wrap">
        <table className="sm__table option-table">
          <thead>
            <tr>
              <SortTh label="Name" field="symbol" align="left" />
              <SortTh label="LTP" field="ltp" />
              <SortTh label="Chg %" field="pctChange" />
              <SortTh label="Volume" field="volume" />
              <SortTh label="Avg Vol (20d)" field="averageVolume20" />
              <SortTh label="Rel Vol" field="relativeVolume" />
              <SortTh label="CMF" field="cmfValue" tooltip={TOOLTIPS?.cmf} />
              <SortTh label="MFI" field="mfiValue" tooltip={TOOLTIPS?.mfi} />
              <SortTh
                label="MACD Signal"
                field="macdSignal"
                tooltip={TOOLTIPS?.rsiMacdSignal}
              />
            </tr>
          </thead>
          <tbody>
            {sorted.map((stock) => (
              <tr
                key={stock.symbol}
                className="sm__row"
                onClick={() =>
                  navigate(`/stockdetail/${stock.symbol}/${stock.securityId}`)
                }
              >
                <td className="sm__td-name">
                  <span className="sm__symbol">{stock.symbol}</span>
                </td>
                <td className="sm__td-num">
                  ₹
                  {stock.ltp?.toLocaleString("en-IN", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </td>
                <td
                  className="sm__td-num"
                  style={{
                    color:
                      stock.pctChange > 0
                        ? "#249966"
                        : stock.pctChange < 0
                          ? "#f87171"
                          : "#64748b",
                  }}
                >
                  {stock.pctChange > 0 ? "+" : ""}
                  {stock.pctChange}%
                </td>
                <td className="sm__td-num">
                  {stock.volume?.toLocaleString("en-IN")}
                </td>
                <td className="sm__td-num">
                  {stock.averageVolume20 > 0
                    ? stock.averageVolume20.toLocaleString("en-IN")
                    : "—"}
                </td>
                <td
                  className={`sm__td-num ${getRvolClass(stock.relativeVolume)}`}
                >
                  {stock.relativeVolume !== null
                    ? `${stock.relativeVolume}x`
                    : "—"}
                </td>
                <td
                  className={`sm__td-num ${getSignalTextClass(stock.cmfSignal)}`}
                >
                  {stock.cmfValue !== null ? stock.cmfValue.toFixed(4) : "—"}
                </td>
                <td
                  className={`sm__td-num ${getSignalTextClass(stock.mfiSignal)}`}
                >
                  {stock.mfiValue !== null ? stock.mfiValue.toFixed(2) : "—"}
                </td>
                <td className="sm__td-num">
                  <span
                    className={`sm__signal-pill ${getSignalTextClass(stock?.macdSignal)}`}
                  >
                    {stock?.macdSignal}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default HighVolumeScreener;
